import type { ApiService } from '@/core/http/services/ApiService'
import type { AxiosResponse } from 'axios'
import type { DataResponse, PaginatedResponse } from '@/core/http/services/types'
import type { PromoProduct, PromoProductRequest } from '@/core/http/services/promo/types'

export class PromoProductService {
  constructor(private readonly service: ApiService) {
    //
  }

  private path(promoId: number, id: number | null = null): string {
    let path = `/api/promos/${promoId}/promo-products`

    if (id) {
      path += `/${id}`
    }

    return path
  }

  async index(promoId: number): Promise<AxiosResponse<PaginatedResponse<PromoProduct[]>>> {
    return this.service.client.get<PaginatedResponse<PromoProduct[]>>(this.path(promoId))
  }

  async show(id: number): Promise<AxiosResponse<DataResponse<PromoProduct>>> {
    return this.service.client.get(`/api/promo-products/${id}`)
  }

  async store(
    promoId: number,
    request: PromoProductRequest,
  ): Promise<AxiosResponse<DataResponse<PromoProduct>>> {
    return this.service.client.post(this.path(promoId), request)
  }

  async update(
    promoId: number,
    id: number,
    request: PromoProductRequest,
  ): Promise<AxiosResponse<DataResponse<PromoProduct>>> {
    return this.service.client.put(this.path(promoId, id), request)
  }

  async destroy(promoId: number, id: number): Promise<AxiosResponse<void>> {
    return this.service.client.delete<void>(this.path(promoId, id))
  }

  async import(promoId: number, file: File): Promise<AxiosResponse<{ imported: number }>> {
    const data = new FormData()
    data.append('file', file)
    return this.service.client.post(`${this.path(promoId)}/import`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  }

  async downloadImportExample(promoId: number): Promise<void> {
    const response = await this.service.client.get<Blob>(`${this.path(promoId)}/import-example`, {
      responseType: 'blob',
    })
    const url = URL.createObjectURL(response.data)
    const link = document.createElement('a')
    link.href = url
    link.download = 'promo-producten.csv'
    document.body.appendChild(link)
    try {
      link.click()
    } finally {
      link.remove()
      URL.revokeObjectURL(url)
    }
  }
}
